import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Alert, Spin, Progress, Empty } from 'antd';
import {
  DollarOutlined,
  RiseOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import adminApi from '../../services/tenantService';
import { DashboardStats, RevenueChartPoint, SubscriptionPlan } from '../../types/tenant';

const AdminRevenuePage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [revenueChart, setRevenueChart] = useState<RevenueChartPoint[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, chartData, plansData] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.getRevenueChart(12),
        adminApi.getPlans(),
      ]);
      setStats(statsData);
      setRevenueChart(chartData);
      setPlans(plansData);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" tip="Loading revenue data\u2026" /></div>;

  const mrr = stats?.mrr || 0;
  const arr = mrr * 12;
  const maxMrr = Math.max(...revenueChart.map(r => r.mrr), 1);

  return (
    <div>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} />}

      {/* Revenue Summary */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card style={{ borderLeft: '4px solid #2E7D32' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Monthly Recurring Revenue" value={mrr} prefix={<DollarOutlined />} precision={0} valueStyle={{ color: '#2E7D32' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderLeft: '4px solid #1976D2' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Annual Recurring Revenue" value={arr} prefix={<RiseOutlined />} precision={0} valueStyle={{ color: '#1976D2' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card style={{ borderLeft: '4px solid #7B1FA2' }} styles={{ body: { padding: 20 } }}>
            <Statistic title="Active Subscribers" value={stats?.activeTenants || 0} prefix={<TeamOutlined />} valueStyle={{ color: '#7B1FA2' }} />
          </Card>
        </Col>
      </Row>

      {/* MRR Trend (CSS bar chart) */}
      <Card title="MRR Trend (Last 12 Months)" style={{ marginBottom: 24 }}>
        {revenueChart.length === 0 ? (
          <Empty description="No revenue data yet" />
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200 }}>
            {revenueChart.map((point) => {
              const height = maxMrr > 0 ? (point.mrr / maxMrr) * 100 : 0;
              return (
                <div key={point.month} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 11, color: '#999', fontVariantNumeric: 'tabular-nums' }}>${point.mrr.toLocaleString()}</span>
                  <div
                    style={{
                      width: '100%',
                      backgroundColor: '#1976D2',
                      borderRadius: '4px 4px 0 0',
                      minHeight: 2,
                      height: `${height}%`,
                      transition: 'height 0.3s',
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#999' }}>{point.month.slice(5)}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Revenue by Plan */}
      <Card title="Revenue by Plan">
        {plans.length === 0 ? (
          <Empty description="No plan data" />
        ) : (
          <div>
            {plans.map((plan) => {
              const revenue = (plan.subscriberCount || 0) * plan.basePrice;
              const percent = mrr > 0 ? Math.round((revenue / mrr) * 100) : 0;
              return (
                <div key={plan.id} style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 16 }}>
                  <span style={{ width: 100, fontWeight: 500, fontSize: 14 }}>{plan.name}</span>
                  <span style={{ width: 100, fontSize: 12, color: '#999' }}>{plan.subscriberCount} subscribers</span>
                  <Progress percent={percent} style={{ flex: 1 }} strokeColor="#1976D2" showInfo={false} />
                  <span style={{ width: 110, textAlign: 'right', fontWeight: 500, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>${revenue.toLocaleString()}/mo</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminRevenuePage;
